import type { ClientAPICall } from '@/app/components/search/common/clientApiType.ts';
import { API_CALL_PREFIX } from '@/app/components/search/utils/api/apiUtils.ts';
import { v4 as uuidv4 } from 'uuid';
import {
  HEADER_NAME_APP_NAME,
  HEADER_NAME_HOST_ID,
  HEADER_NAME_MPT,
  HEADER_NAME_SPAN_ID,
  HEADER_NAME_TRACE_ID,
} from '@lmn/nextjs-appfw/dist/common';
import { APP_NAME } from '@/utils/appUtils.ts';
import { TSX_REQUEST_HEADER_NAME } from '@/app/pageTypes.ts';
import type { TabCredentials } from '@/app/common/casperToken/tabCasperToken.ts';
import {
  getFreshTabCredentials,
  getTabCredentials,
  reportRenewSignalFromResponse,
} from '@/app/common/casperToken/tabCasperToken.ts';
import { fetchWithRetryTimeout } from '../utils/fetchClient.ts';

const createHeadersClientAPICall = (
  headers: { [key: string]: string },
  traceParent: string,
  { mpt, csrfToken }: TabCredentials
) => {
  const traceId = uuidv4();
  const tracingHeaders = {
    [HEADER_NAME_TRACE_ID]: traceId,
    [HEADER_NAME_APP_NAME]: APP_NAME,
    [HEADER_NAME_SPAN_ID]: '0000000',
    [HEADER_NAME_HOST_ID]: 'browser',
  };
  const traceParentHeader = traceParent ? { [TSX_REQUEST_HEADER_NAME.TRACE_PARENT]: traceParent } : undefined;
  const mptHeader = mpt ? { [HEADER_NAME_MPT]: mpt } : undefined;
  const csrfTokenHeader = csrfToken ? { [TSX_REQUEST_HEADER_NAME.CSRF_TOKEN]: csrfToken } : undefined;
  return { ...headers, ...tracingHeaders, ...traceParentHeader, ...mptHeader, ...csrfTokenHeader };
};

const clientAPICall = async <ReqPayload, Res>({
  apiName,
  payload,
  defaultResponse,
  headers = {},
  traceParent,
  commonData,
  skipPreflight = false,
  ignoreRenewSignal = false,
}: ClientAPICall<ReqPayload, Res>): Promise<Res> => {
  const url = `${API_CALL_PREFIX}${apiName}`;
  let response = defaultResponse || ({} as Res);

  try {
    // Serialised before any wait, so the call sends the payload as it was when it was made.
    const body = JSON.stringify(payload);
    // MPH-1410: the tab's current pair, renewed first when it is about to expire or a renewal is already in flight
    // (unless the caller cannot wait); commonData's while the tab-token module is not started.
    const credentials = skipPreflight ? getTabCredentials(commonData) : await getFreshTabCredentials(commonData);
    const headersClientAPICall = createHeadersClientAPICall(headers, traceParent, credentials);
    response = await fetchWithRetryTimeout<Res>({
      url,
      body,
      headers: headersClientAPICall,
      timeout: 20000,
      onResponse: ignoreRenewSignal
        ? undefined
        : (answer: Response | undefined) => reportRenewSignalFromResponse(answer, credentials.mpt),
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // TODO: decouple from clientAppLog
    // clientAppLog({
    //   level: 'WARN',
    //   codeSourceName: 'clientAPICall',
    //   logMessage: `apiName:${apiName}, payload:${JSON.stringify(payload)}, error: ${error.toString()}`,
    //   url,
    // });
  }

  return response;
};

export default clientAPICall;
